/**
 * Phone Number Validation and Formatting Utilities
 * Supports E.164 format for international phone numbers
 */

export interface PhoneValidationResult {
  valid: boolean;
  formatted?: string;
  error?: string;
}

/**
 * Validates and formats a US phone number to E.164 format
 * Accepts formats like:
 * - (313) 758-7217
 * - 313-758-7217
 * - 313.758.7217
 * - 3137587217
 * - +13137587217
 */
export function validateAndFormatUSPhone(input: string): PhoneValidationResult {
  if (!input) {
    return { valid: false, error: 'Phone number is required' };
  }

  // Remove all non-digit characters except leading +
  const cleanedInput = input.replace(/[^\d+]/g, '');

  // Handle different input formats
  let digits = cleanedInput;

  // If it starts with +1, remove it for validation
  if (digits.startsWith('+1')) {
    digits = digits.substring(2);
  }
  // If it starts with 1 and has 11 digits total, remove the 1
  else if (digits.startsWith('1') && digits.length === 11) {
    digits = digits.substring(1);
  }

  // Validate US phone number (must be 10 digits)
  if (digits.length !== 10) {
    return {
      valid: false,
      error: 'US phone numbers must be 10 digits',
    };
  }

  // Validate area code (first digit cannot be 0 or 1)
  const areaCode = digits.substring(0, 3);
  if (areaCode[0] === '0' || areaCode[0] === '1') {
    return {
      valid: false,
      error: 'Invalid area code',
    };
  }

  // Format to E.164 (international format)
  const formatted = `+1${digits}`;

  return {
    valid: true,
    formatted,
  };
}

/**
 * Validates phone number in E.164 format
 * E.164 format: +[country code][subscriber number]
 * Example: +13137587217
 */
export function validateE164Format(phoneNumber: string): PhoneValidationResult {
  if (!phoneNumber) {
    return { valid: false, error: 'Phone number is required' };
  }

  // E.164 regex: + followed by 1-15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;

  if (!e164Regex.test(phoneNumber)) {
    return {
      valid: false,
      error: 'Phone number must be in E.164 format (e.g., +13137587217)',
    };
  }

  return {
    valid: true,
    formatted: phoneNumber,
  };
}

/**
 * Formats a phone number for display
 * Converts E.164 format to human-readable format
 * Example: +13137587217 -> (313) 758-7217
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
  if (!phoneNumber) return '';

  // Check if it's a US number (+1 followed by 10 digits)
  const usPhoneRegex = /^\+1(\d{3})(\d{3})(\d{4})$/;
  const match = phoneNumber.match(usPhoneRegex);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  // For non-US numbers, just return as-is
  return phoneNumber;
}

/**
 * Generates a random 6-digit OTP code
 */
export function generateOTP(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Validates OTP code format
 */
export function validateOTP(otp: string): PhoneValidationResult {
  if (!otp) {
    return { valid: false, error: 'OTP code is required' };
  }

  if (!/^\d{6}$/.test(otp)) {
    return {
      valid: false,
      error: 'OTP must be a 6-digit code',
    };
  }

  return { valid: true };
}

/**
 * Checks if a phone number matches a specific format pattern
 */
export function matchesPhonePattern(input: string, pattern: 'US' | 'E164'): boolean {
  if (pattern === 'US') {
    const usPattern = /^(\+1)?[\s.-]?\(?([0-9]{3})\)?[\s.-]?([0-9]{3})[\s.-]?([0-9]{4})$/;
    return usPattern.test(input);
  }

  if (pattern === 'E164') {
    const e164Pattern = /^\+[1-9]\d{1,14}$/;
    return e164Pattern.test(input);
  }

  return false;
}

/**
 * Sanitizes phone number input for database storage
 * Ensures phone number is in E.164 format
 */
export function sanitizePhoneForStorage(input: string): PhoneValidationResult {
  // First try to validate as E.164
  const e164Result = validateE164Format(input);
  if (e164Result.valid) {
    return e164Result;
  }

  // If not E.164, try to format as US phone number
  return validateAndFormatUSPhone(input);
}

/**
 * Checks if OTP is expired
 */
export function isOTPExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return true;
  return new Date(expiresAt) < new Date();
}

/**
 * Creates an OTP expiration timestamp (default: 10 minutes from now)
 */
export function createOTPExpiration(minutes: number = 10): string {
  const expiration = new Date();
  expiration.setMinutes(expiration.getMinutes() + minutes);
  return expiration.toISOString();
}
